import { FuAdminRoleType } from '@faceup/utils'

type FuAdminAccessArea = {
  label: string
  kind: 'page' | 'action'
  roles: readonly FuAdminRoleType[]
}

/**
 * What each role unlocks in kredenc. The menu, the routes, `AdminAccessRightsProvider` and the
 * role matrix on the Internal users page all read this, so they cannot drift apart. The backend
 * keeps its own `allowUserRole` lists, which have to be kept in line with it by hand.
 */
export const fuAdminAccess = {
  companies: {
    label: 'Companies',
    kind: 'page',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  schools: {
    label: 'Schools',
    kind: 'page',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager, FuAdminRoleType.Psychologist],
  },
  partners: {
    label: 'Partners',
    kind: 'page',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  cases: {
    label: 'Cases, including report content',
    kind: 'page',
    roles: [FuAdminRoleType.Psychologist],
  },
  materials: {
    label: 'Materials',
    kind: 'page',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  aiPrompts: {
    label: 'AI prompts',
    kind: 'page',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  hotlines: {
    label: 'Hotlines',
    kind: 'page',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  vonageHotlines: {
    label: 'Vonage hotlines',
    kind: 'page',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  sendEmails: {
    label: 'Send emails',
    kind: 'page',
    roles: [FuAdminRoleType.Admin],
  },
  regionMaintenance: {
    label: 'Region maintenance',
    kind: 'page',
    roles: [FuAdminRoleType.Admin],
  },
  integrationHttpLogs: {
    label: 'Integration HTTP logs',
    kind: 'page',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.IntegrationDeveloper],
  },
  internalUsers: {
    label: 'Internal users',
    kind: 'page',
    roles: [FuAdminRoleType.Admin],
  },
  institutionEdit: {
    label: 'Edit an institution',
    kind: 'action',
    roles: [FuAdminRoleType.Admin],
  },
  institutionMemberActions: {
    label: 'Edit, delete and transfer members',
    kind: 'action',
    roles: [FuAdminRoleType.Admin],
  },
  basicMemberActions: {
    label: 'Resend a member invitation',
    kind: 'action',
    roles: [FuAdminRoleType.Manager],
  },
  institutionLink: {
    label: 'Link an institution to a partner',
    kind: 'action',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  createPaymentUrl: {
    label: 'Create a payment URL',
    kind: 'action',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  demoCompany: {
    label: 'Create a demo company',
    kind: 'action',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  partnerApprove: {
    label: 'Approve a partner registration',
    kind: 'action',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  partnerDelete: {
    label: 'Delete a partner',
    kind: 'action',
    roles: [FuAdminRoleType.Admin],
  },
  introwActivation: {
    label: 'Activate Introw',
    kind: 'action',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Manager],
  },
  approveOrRejectSchool: {
    label: 'Approve or reject a school',
    kind: 'action',
    roles: [FuAdminRoleType.Admin, FuAdminRoleType.Psychologist],
  },
} as const satisfies Record<string, FuAdminAccessArea>

export type FuAdminAccessKey = keyof typeof fuAdminAccess

/** An admin holding several roles gets everything any of them unlocks. */
export const canAccess = (roles: readonly FuAdminRoleType[], key: FuAdminAccessKey) =>
  fuAdminAccess[key].roles.some(role => roles.includes(role))

export const fuAdminRoleLabels: Record<FuAdminRoleType, string> = {
  [FuAdminRoleType.Admin]: 'Admin',
  [FuAdminRoleType.Manager]: 'Manager',
  [FuAdminRoleType.Psychologist]: 'Psychologist',
  [FuAdminRoleType.IntegrationDeveloper]: 'Integration developer',
}
